<template>
    <div
        v-if="filteredTableData.length === 0"
        class="q-mt-xl bg-white q-pa-md border-grey"
    >
        <q-icon name="fal fa-exclamation-triangle" size="md" />
        <span style="margin-left: 10px">
            {{ $t('No records found for this date range.') }}
        </span>
    </div>

    <div v-else class="full-height">
        <!-- Multi-asset Export buttons-->
        <div class="flex justify-end">
            <q-btn-dropdown
                v-if="!$q.platform.is.mobile && isMultiAsset"
                id="report-data-dropdown"
                color="primary"
                icon="far fa-file-arrow-down"
                :label="$t('Export All Data')"
                size="12px"
                dropdown-icon="arrow_drop_down"
                outline
            >
                <div class="q-pa-md" style="min-width: 300px">
                    <q-btn-group spread unelevated>
                        <q-btn
                            id="csv-button"
                            label="CSV"
                            color="accent"
                            @click="selectedFileType = 'CSV'"
                            :outline="selectedFileType !== 'CSV'"
                        />
                        <q-btn
                            id="xls-button"
                            label="XLS"
                            color="accent"
                            @click="selectedFileType = 'XLS'"
                            :outline="selectedFileType !== 'XLS'"
                        />
                    </q-btn-group>

                    <div class="q-mt-md">
                        <q-radio
                            v-model="reportBreakDownBy"
                            :label="$t('Group production by product')"
                            class="product-radio"
                            val="product"
                            color="accent"
                        />
                    </div>
                    <div class="q-mb-md">
                        <q-radio
                            v-model="reportBreakDownBy"
                            :label="
                                $t(
                                    'Group production totals by selected resolution'
                                )
                            "
                            class="period-radio"
                            val="period"
                            color="accent"
                        />
                    </div>
                    <div class="row justify-end">
                        <q-btn
                            id="cancel-report-button"
                            @click="selectedFileType = 'CSV'"
                            :label="$t('Cancel')"
                            class="q-mr-md"
                            color="accent"
                            v-close-popup
                            outline
                        />
                        <q-btn
                            id="download-report-button"
                            @click="exportReportData()"
                            label="Download"
                            color="accent"
                            :loading="isDownloadingXLS || isDownloadingCSV"
                        />
                    </div>
                </div>
            </q-btn-dropdown>
        </div>
        <div
            id="trends-report"
            v-for="table in filteredTableData"
            :key="table.asset_id"
            class="q-my-lg"
        >
            <div
                class="row bg-primary q-px-md border-sides"
                :class="
                    !isAssetInGraphMode(table.asset_id) ? 'q-pt-sm' : 'q-py-sm'
                "
            >
                <!-- Asset Name -->
                <div class="col-6 text-white text-h6 text-bold">
                    <div class="flex items-center">
                        <q-btn-group class="q-mr-sm">
                            <q-btn
                                @click="
                                    assetsInGraphMode.delete(table.asset_id)
                                "
                                :color="
                                    !isAssetInGraphMode(table.asset_id)
                                        ? 'primary'
                                        : 'white'
                                "
                                :text-color="
                                    !isAssetInGraphMode(table.asset_id)
                                        ? 'white'
                                        : 'primary'
                                "
                                class="toggle-report-view-button"
                                icon="fas fa-table"
                                size="10px"
                                :outline="isAssetInGraphMode(table.asset_id)"
                            >
                                <q-tooltip>
                                    {{ $t('Switch to table view') }}
                                </q-tooltip>
                            </q-btn>
                            <q-btn
                                @click="assetsInGraphMode.add(table.asset_id)"
                                :color="
                                    !isAssetInGraphMode(table.asset_id)
                                        ? 'white'
                                        : 'primary'
                                "
                                :text-color="
                                    !isAssetInGraphMode(table.asset_id)
                                        ? 'primary'
                                        : 'white'
                                "
                                class="toggle-report-view-button"
                                icon="fas fa-chart-bar"
                                size="10px"
                                :outline="!isAssetInGraphMode(table.asset_id)"
                            >
                                <q-tooltip>
                                    {{ $t('Switch to chart view') }}
                                </q-tooltip>
                            </q-btn>
                        </q-btn-group>
                        {{ assetIdToName[table.asset_id] }}
                        <q-badge
                            v-if="isAssetDeleted(table.asset_id)"
                            color="lighter-grey"
                            text-color="medium-grey"
                            class="q-ml-sm"
                        >
                            {{ $t('Deleted') }}
                        </q-badge>
                    </div>
                </div>
                <!-- Per-asset Export buttons -->
                <div class="col-6">
                    <div class="row justify-end">
                        <q-btn-dropdown
                            v-if="!$q.platform.is.mobile && !isMultiAsset"
                            id="report-data-dropdown"
                            text-color="white"
                            icon="far fa-file-arrow-down"
                            :label="$t('Report')"
                            class="download-file-buttons"
                            size="12px"
                            flat
                        >
                            <div class="q-pa-md" style="min-width: 300px">
                                <q-btn-group spread unelevated>
                                    <q-btn
                                        id="csv-button"
                                        label="CSV"
                                        color="accent"
                                        @click="selectedFileType = 'CSV'"
                                        :outline="selectedFileType !== 'CSV'"
                                    />
                                    <q-btn
                                        id="xls-button"
                                        label="XLS"
                                        color="accent"
                                        @click="selectedFileType = 'XLS'"
                                        :outline="selectedFileType !== 'XLS'"
                                    />
                                </q-btn-group>

                                <div class="q-mt-md">
                                    <q-radio
                                        v-model="reportBreakDownBy"
                                        :label="
                                            $t('Group production by product')
                                        "
                                        class="product-radio"
                                        val="product"
                                        color="accent"
                                    />
                                </div>
                                <div class="q-mb-md">
                                    <q-radio
                                        v-model="reportBreakDownBy"
                                        :label="
                                            $t(
                                                'Group production totals by selected resolution'
                                            )
                                        "
                                        class="period-radio"
                                        val="period"
                                        color="accent"
                                    />
                                </div>
                                <div class="row justify-end">
                                    <q-btn
                                        id="cancel-report-button"
                                        @click="selectedFileType = 'CSV'"
                                        :label="$t('Cancel')"
                                        class="q-mr-md"
                                        color="accent"
                                        v-close-popup
                                        outline
                                    />
                                    <q-btn
                                        id="download-report-button"
                                        @click="
                                            exportReportData([table.asset_id])
                                        "
                                        label="Download"
                                        color="accent"
                                        :loading="
                                            isDownloadingXLS || isDownloadingCSV
                                        "
                                    />
                                </div>
                            </div>
                        </q-btn-dropdown>
                    </div>
                </div>
            </div>
            <!-- Table View-->
            <trends-report-table
                v-if="!isAssetInGraphMode(table.asset_id)"
                :rows="table.rows"
                :columns="table.columns"
            />
            <!-- Chart View-->
            <trends-report-graph
                v-else
                :series="graphData(table.asset_id)"
                :reportResolution="getResolution"
            />
        </div>
    </div>
</template>
<script>
import { convertToUnitSystem } from '@models/products.js';
import { getLocaleFormat } from '@models/timeformats.js';
import FileExporter from '@utils/FileExporter.js';
import { logFileDownloadMessage } from '@utils/logMessagesForE2ETesting.js';
import { mapState } from 'pinia';
import { useAssetsStore } from '@stores/assets.ts';
import { useFacilitiesStore } from '@stores/facilities.ts';
import TrendsReportBuilder from './trendsReportBuilder.js';
import TrendsReportGraph from './TrendsReportGraph.vue';
import TrendsReportTable from './TrendsReportTable.vue';
import { useReportsStore } from '@stores/reports.ts';
import { useAuthStore } from '@stores/auth';

export default {
    name: 'TrendsReport',
    components: {
        TrendsReportTable,
        TrendsReportGraph
    },
    props: {
        records: {
            required: true,
            validator(val) {
                return val instanceof Object || val === null;
            }
        },
        filteredAssets: {
            type: Set,
            required: false
        },
        reportTimezone: {
            type: String,
            required: true
        },
        assetIdToName: {
            type: Object,
            required: true
        },
        facilityId: {
            type: Number,
            required: true
        }
    },
    mounted() {
        this.createReportData();
    },
    data() {
        return {
            reportBuilder: null,
            tableData: [],
            fileRowsData: null,

            // Export file loading states
            isDownloadingCSV: false,
            isDownloadingXLS: false,
            // Assets in graph mode
            assetsInGraphMode: new Set(),
            // Export dropdown state
            selectedFileType: 'XLS',
            reportBreakDownBy: 'product'
        };
    },
    computed: {
        ...mapState(useFacilitiesStore, ['getFacility']),
        ...mapState(useAssetsStore, ['getAsset']),
        ...mapState(useReportsStore, {
            getDateRange: 'dateRange',
            getResolution: 'resolution',
            selectedTimezone: 'selectedTimezone'
        }),
        ...mapState(useAuthStore, {
            userLocale: 'getUserLocale'
        }),
        /**
         * Check if report contains data for multiple assets
         * @return {Boolean} true if report contains data for multiple assets
         */
        isMultiAsset() {
            return this.tableData.length > 1;
        },
        /**
         * Get the table data filtered by the selected assets.
         * @returns {Array}
         */
        filteredTableData() {
            if (this.filteredAssets === null) {
                return this.tableData;
            }
            // Only return the table data for the selected assets.
            return this.tableData.filter(table =>
                this.filteredAssets.has(table.asset_id)
            );
        },
        /**
         * Extract facility name from facility id
         * @return {String} facility name
         */
        facilityName() {
            return this.getFacility(this.facilityId).name;
        },
        /**
         * Get the unit system used by the facility
         * @return {String} unit system
         */
        unitSystem() {
            return this.getFacility(this.facilityId)?.unit_system;
        },
        /**
         * Get the file headers for file exporting
         * @return {Array} file headers for file exporting
         */
        fileHeaders() {
            const headers = [
                this.$t('Date').concat(` [${this.selectedTimezone}]`),
                this.$t('Facility'),
                this.$t('Asset'),
                this.$t('Product'),
                this.$t('OEE [%]'),
                this.$t('Availability [%]'),
                this.$t('Performance [%]'),
                this.$t('Quality [%]'),
                this.$t('Total production (products)'),
                this.$t('Total quantity'),
                this.$t('Net production (products)'),
                this.$t('Net quantity')
            ];

            // Add run rate and unit measure headers if breakdown by product
            if (this.reportBreakDownBy === 'product') {
                headers.push(this.$t('Run Rate (product/min)'));
                headers.push(this.$t('Unit measure'));
            }

            // Append new headers for the additional metrics
            headers.push(this.$t('Throughput (products/min)'));
            headers.push(this.$t('Throughput (quantity/min)'));
            headers.push(this.$t('Running time (min)'));
            headers.push(this.$t('Available production time (min)'));

            return headers;
        }
    },
    methods: {
        /**
         * Create report data.
         */
        createReportData() {
            // Since comparison reports are not supported in this report type,
            // we only deal with the current report data.
            let allRecords = this.records['currentReportData'];

            // convert the unit packing and unit measure to the facility's unit system
            allRecords = this.convertRecordsToFacilityUnitSystem(allRecords);

            // if there are records, build the report
            if (allRecords.length > 0) {
                this.reportBuilder = new TrendsReportBuilder({
                    records: allRecords,
                    startDateUTC: this.getDateRange.start, // report field dates are in UTC
                    endDateUTC: this.getDateRange.end, // report field dates are in UTC
                    timezone: this.reportTimezone,
                    resolution: this.getResolution,
                    userLocale: this.userLocale,
                    assetIdToName: this.assetIdToName,
                    facilityName: this.facilityName // for exporting the file
                });
                this.reportBuilder.buildReport();

                this.tableData = this.reportBuilder.getTableData();
            }
        },
        /**
         * Convert records' unit packing and unit measure to the facility's unit system
         * @param {Array} records
         * @return {Array} records with converted unit packing and unit measure
         */
        convertRecordsToFacilityUnitSystem(records) {
            return records.map(record => {
                const {
                    value: converted_unit_packing,
                    unit: converted_unit_measure
                } = convertToUnitSystem(
                    record.unit_packing,
                    record.unit_measure,
                    this.unitSystem
                );
                record.unit_packing = converted_unit_packing;
                record.unit_measure = converted_unit_measure;
                return record;
            });
        },
        /**
         * Export file as CSV.
         * @param {Array} asset_ids - Array of asset IDs
         */
        exportDataAsCSV(asset_ids) {
            this.isDownloadingCSV = true;

            // initialize file exporter instance
            this.fileExporter = new FileExporter({
                headers: this.fileHeaders
            });

            const start_date = this.formatDate(this.getDateRange.start);
            const end_date = this.formatDate(this.getDateRange.end);

            let combinedFileRowsData = [];

            asset_ids.forEach(asset_id => {
                let fileRowsData;
                if (this.reportBreakDownBy === 'period') {
                    fileRowsData =
                        this.reportBuilder.getFileRowsByPeriod(asset_id);
                } else {
                    fileRowsData = this.reportBuilder.getFileRowsData(
                        asset_id,
                        this.unitSystem
                    );
                }

                combinedFileRowsData =
                    combinedFileRowsData.concat(fileRowsData);
            });

            try {
                this.fileExporter.exportCSV({
                    filename: `${this.facilityName}_${this.$t('Trends')}_by_${this.$t(this.reportBreakDownBy)}_${start_date}_${end_date}`,
                    rows: combinedFileRowsData
                });
                // log message for E2E testing
                logFileDownloadMessage('CSV', this.reportBreakDownBy);
            } catch (error) {
                this.$notifyError(
                    this.$t(
                        'An error has occurred during file export. Please try again.'
                    )
                );
            } finally {
                this.isDownloadingCSV = false;
            }
        },
        /**
         * Export file as XLS.
         * @param {Array} asset_ids - Array of asset IDs
         */
        exportDataAsXLS(asset_ids) {
            this.isDownloadingXLS = true;

            // initialize file exporter instance
            this.fileExporter = new FileExporter({
                headers: this.fileHeaders
            });

            const start_date = this.formatDate(this.getDateRange.start);
            const end_date = this.formatDate(this.getDateRange.end);

            let combinedFileRowsData = [];

            asset_ids.forEach(asset_id => {
                let fileRowsData;
                if (this.reportBreakDownBy === 'period') {
                    fileRowsData =
                        this.reportBuilder.getFileRowsByPeriod(asset_id);
                } else {
                    fileRowsData = this.reportBuilder.getFileRowsData(
                        asset_id,
                        this.unitSystem
                    );
                }
                combinedFileRowsData =
                    combinedFileRowsData.concat(fileRowsData);
            });

            try {
                this.fileExporter.exportXLS({
                    filename: `${this.facilityName}_${this.$t('Trends')}_by_${this.$t(this.reportBreakDownBy)}_${start_date}_${end_date}`,
                    sheetName: this.$t('Trends Report'),
                    rows: combinedFileRowsData
                });
                // log message for E2E testing
                logFileDownloadMessage('XLS', this.reportBreakDownBy);
            } catch (error) {
                this.$notifyError(
                    this.$t(
                        'An error has occurred during file export. Please try again.'
                    )
                );
            } finally {
                this.isDownloadingXLS = false;
            }
        },
        /**
         * Format a date to local date format
         * @param {String} datetime
         * @return {String} datetime in given locale.
         */
        formatDate(datetime) {
            return getLocaleFormat(datetime, this.userLocale);
        },
        /**
         * Verify if the asset is deleted
         * @return {Boolean} true if the asset is deleted
         */
        isAssetDeleted(assetId) {
            const asset = this.getAsset(assetId);
            if (asset && asset.is_deleted) {
                return asset.is_deleted;
            }
            return false;
        },
        /**
         * Get the graph data for a particular asset
         * @param {Number} assetId
         * @return {Array} Graph data
         */
        graphData(assetId) {
            return this.reportBuilder.getGraphData(assetId);
        },
        /**
         * Check if the asset is in graph mode
         * @param {Number} assetId
         * @return {Boolean} true if the asset is in graph mode
         */
        isAssetInGraphMode(assetId) {
            return this.assetsInGraphMode.has(assetId);
        },
        /**
         * Export report data to a file based on the selected file type.
         * @param {Array} assetIds - Array of asset IDs
         */
        exportReportData(assetIds) {
            let selectedAssets = [];
            if (!assetIds || assetIds.length === 0) {
                selectedAssets = this.filteredTableData.map(
                    asset => asset.asset_id
                );
            } else {
                selectedAssets = assetIds;
            }
            if (this.selectedFileType === 'CSV') {
                this.exportDataAsCSV(selectedAssets);
            } else if (this.selectedFileType === 'XLS') {
                this.exportDataAsXLS(selectedAssets);
            }
        }
    }
};
</script>

<style lang="scss" scoped>
// Add the border sides class to the export buttons container
// to match the table border sides for consistency.
.border-sides {
    border-left: 0.5px solid $grey-3;
    border-right: 0.5px solid $grey-3;
}
</style>
